/**
 * Admin-editable copy (SCRUM-146): the legal documents and the transactional-mail strings.
 *
 * The public read is deliberately tolerant — see `fetchLegalOverride`. The admin calls are ordinary
 * authenticated requests, gated server-side by `AdminGuard`.
 */
import { authFetch, parseOrThrow, API_BASE_URL } from "./http";

/** What kind of copy is being overridden: a legal document, a mail string, an interface string. */
export type ContentScope = "legal" | "mail" | "ui";

export interface LegalDocView {
  key: "privacy" | "terms";
  locale: string;
  override: string | null;
  updatedAt: string | null;
}

export interface MailStringView {
  key: string;
  locale: string;
  default: string;
  override: string | null;
  placeholders: string[];
}

/**
 * The admin's replacement for a document, or `null` for "use the copy in this bundle".
 *
 * Never throws: a network failure, a 500, or a malformed body all resolve to `null`, which the
 * caller renders as the bundled document. The legal pages are read by people deciding whether to
 * accept the terms — an API hiccup must show them the text we shipped, not an error page.
 */
export async function fetchLegalOverride(doc: "privacy" | "terms"): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/content/legal/${doc}`);
    if (!res.ok) return null;
    const body = (await res.json()) as { markdown?: unknown };
    return typeof body.markdown === "string" ? body.markdown : null;
  } catch {
    return null;
  }
}

/**
 * Interface-string overrides for one locale. Public, unauthenticated, and — like
 * `fetchLegalOverride` — never throws: on any failure the app keeps the wording it shipped with,
 * which is a correct interface, not a broken one.
 */
export async function fetchUiOverrides(locale: string): Promise<Record<string, string>> {
  try {
    const res = await fetch(`${API_BASE_URL}/content/ui?locale=${encodeURIComponent(locale)}`);
    if (!res.ok) return {};
    const body = (await res.json()) as { overrides?: unknown };
    const raw = body.overrides;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return Object.fromEntries(
      Object.entries(raw as Record<string, unknown>).filter(([, v]) => typeof v === "string")
    ) as Record<string, string>;
  } catch {
    return {};
  }
}

export const listUiOverrides = (locale: string): Promise<{ overrides: Record<string, string> }> =>
  getJson(`/admin/content/ui?locale=${encodeURIComponent(locale)}`);

async function getJson<T>(path: string): Promise<T> {
  return parseOrThrow<T>(await authFetch(path));
}

export const listLegalDocs = (): Promise<{ docs: LegalDocView[] }> => getJson("/admin/content/legal");

export const listMailStrings = (locale: string): Promise<{ locale: string; strings: MailStringView[] }> =>
  getJson(`/admin/content/mail?locale=${encodeURIComponent(locale)}`);

export const listContentHistory = (
  scope: ContentScope,
  key: string,
  locale: string
): Promise<{ versions: Array<{ id: number; value: string; createdAt: string }> }> =>
  getJson(`/admin/content/history/${scope}/${encodeURIComponent(key)}?locale=${encodeURIComponent(locale)}`);

export const saveContent = async (
  scope: ContentScope,
  key: string,
  locale: string,
  value: string
): Promise<void> => {
  await parseOrThrow(
    await authFetch(`/admin/content/${scope}/${encodeURIComponent(key)}?locale=${encodeURIComponent(locale)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
    })
  );
};

export const resetContent = async (scope: ContentScope, key: string, locale: string): Promise<void> => {
  await parseOrThrow(
    await authFetch(`/admin/content/${scope}/${encodeURIComponent(key)}?locale=${encodeURIComponent(locale)}`, {
      method: "DELETE",
    })
  );
};
